<template>
  <div class="flex flex-col justify-between">
    <h1 class="w-full bg-orange-academy-300 text-white text-center
    tracking-widest uppercase py-6 text-lg mb-8">
      {{$t('bookingSlots.title')}}
    </h1>
    <div class="w-3/4 mx-auto flex flex-col justify-between text-lg mb-8 font-sans-serif">
      <div class="shadow-lg pt-2 text-center">
        <div class="mb-2">
          {{$t('bookingSlots.subtitle')}} <strong>
            {{ selectedEvent && selectedEvent.start.format('DD-MM-YYYY') }}</strong>
        </div>
        <div class="mb-2">
          {{$t('bookingSlots.from')}}  <strong>
            {{ selectedEvent && selectedEvent.start.formatTime() }}</strong>
          {{$t('bookingSlots.to')}} <strong>
            {{ selectedEvent && selectedEvent.end.formatTime() }}</strong>
        </div>
      </div>
      <div class="flex flex-col"
      v-if="tiers && tiers.length > 0 && teacher.languages">
        <div class="flex flex-col items-center lg:items-start w-full
        justify-between mt-10">
          <div class=" w-full">
            <span class="text-left text-md pt-10 font-medium">
              {{$t('bookingSlots.lesson-tier')}}:</span>
            <ul class="flex flex-col mt-4 w-full justify-between items-start mb-8">
              <li v-for="tier in tiers" :key="tier.id" class="flex w-full justify-start items-center
                border-b my-1 border-grey-academy-150">
                <input role="checkbox" type="radio" :value="tier"
                :id="tier.id" v-model="selectedTier">
                <label :for="tier.id" class="flex items-center ml-3 justify-between w-full">
                  {{tier.name}}
                  <span v-if="tier.package" class="text-sm pl-2">
                  ( {{$t('bookingSlots.pack')}} {{tier.no_lessons}} {{$t('bookingSlots.lessons')}})
                  </span>
                  <div class="flex border border-grey-academy-150 rounded-md p-1 ml-6 text-sm
                    bg-grey-academy-150 text-white font-bold shadow-lg w-20 mb-2 justify-center">
                    {{ formatter.format(tier.price/100) }}/h
                  </div>
                </label>
              </li>
            </ul>
          </div>
          <div class="w-full">
            <span class="text-left text-md pt-3 font-medium" v-if="teacher.languages">
              {{$t('bookingSlots.lang')}}
            </span>
            <ul class="flex flex-col mt-4 w-full justify-between items-start mb-8">
              <li v-for="lang in languages" :key="lang.code"
              class="flex w-full justify-start items-center
                border-b my-1 border-grey-academy-150">
                <input role="checkbox" type="radio" :value="lang"
                :id="lang.code" v-model="selectedLang">
                <label :for="lang.code" class="flex items-center ml-3 justify-between w-full">
                  {{lang.name}}
                </label>
              </li>
            </ul>
          </div>
        </div>
        <div class="flex flex-col mb-8">
          <span class="text-left text-md pt-3 font-medium" v-if="teacher.languages">
              {{$t('bookingSlots.comments')}}
            </span>
          <textarea class="w-full border border-grey-academy-150" rows="3" v-model="comments" />
        </div>
        <div class="flex flex-col" v-if="user.student.default_payment_method">
          <div class="flex text-sm w-full self-start my-3">
            <input type="checkbox" v-model="useDefault" id="use-default">
            <label class="pl-2" for="use-default">{{$t('bookingSlots.default')}}</label>
          </div>
          <default-payment-method class="mt-2 mb-4" v-if="useDefault" />
        </div>
        <div class="text-sm mt-6 flex flex-col" v-if="teacher.cancellation_deadline">
          <span>
            {{ $t('bookingSlots.cancellationDeadline') }}
            {{ teacher.cancellation_deadline }}
            {{ $t('bookingSlots.hoursFromLesson') }}
          </span>
          <span v-if="lessonIsOutOfCancellationDeadline" class="font-bold">
            {{ $t('bookingSlots.warning') }}
            {{ teacher.cancellation_deadline }}
            {{ $t('bookingSlots.noFreeCancellation') }}
          </span>
        </div>
      </div>
      <div v-else class="py-10 text-center text-tiers-#B71C1C">
        {{ $t('bookingSlots.cannotBook') }}
      </div>

      <div class="flex flex-row justify-between mt-6 text-sm">
        <modal-button @click="cancel(selectedEvent.id)"
        :buttonPlaceholder="$t('bookingSlots.cancel')"
        buttonClass="uppercase" />
        <modal-button v-if="selectedTier && selectedLang"
          @click="checkStudentPaymentMethod(selectedEvent)"
          :isLoading="isLoading"
          :buttonPlaceholder="$t('bookingSlots.book')"
          buttonClass="uppercase" />
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapActions, mapGetters } from 'vuex';
import dayjs from 'dayjs';
import DefaultPaymentMethod from '../payment/DefaultPaymentMethod.vue';
import ModalButton from '../graphics/ModalButton.vue';

export default {
  props: ['selectedEvent', 'tiers', 'teacher'],

  data() {
    return {
      selectedTier: '',
      selectedLang: '',
      comments: '',
      useDefault: true,
      isLoading: false,
    };
  },

  components: {
    DefaultPaymentMethod,
    ModalButton,
  },

  computed: {
    ...mapGetters({
      user: 'auth/user',
    }),

    languages() {
      return this.teacher.languages ? JSON.parse(this.teacher.languages) : [];
    },

    lessonIsOutOfCancellationDeadline() {
      return dayjs.tz()
        .isBetween(dayjs.tz(this.selectedEvent.start).subtract(this.teacher.cancellation_deadline, 'h'),
          dayjs.tz(this.selectedEvent.start));
    },
  },

  methods: {
    ...mapActions({
      updatedSlots: 'slots/updatedSlots',
      setChoosenTimezone: 'timezone/setChoosenTimezone',
    }),

    book(id) {
      this.isLoading = true;
      axios.put(`/api/slots/${id}/booking`,
        { tier_id: this.selectedTier.id, lang: this.selectedLang.name, comments: this.comments })
        .then(({ data }) => {
          axios.put(`/api/lessons/${data.booking_id}/update-payment-method?pay_now=${this.lessonIsOutOfCancellationDeadline}`)
            .then((response) => {
              if (response.data !== null && response.data !== '') {
                this.$emit('booked', {
                  slot: id,
                  color: this.selectedTier.color,
                });
                if (this.user.student.default_payment_method === 'paypal') {
                  this.redirectToPaypal(response.data);
                  return;
                }
                this.isLoading = false;
                this.$modal.hide('open-slot');
                this.createNotification('success', this.$t('bookingSlots.booked'));
              } else this.updatedSlots(true);
            }).catch((errors) => {
              this.isLoading = false;
              console.log(errors);
            });
        });
    },

    checkStudentPaymentMethod(slot) {
      if (this.user.student.default_payment_method && this.useDefault) {
        this.book(slot.id);
        return;
      }
      this.isLoading = true;
      axios.put(`/api/slots/${slot.id}/booking`,
        { tier_id: this.selectedTier.id, lang: this.selectedLang.name, comments: this.comments })
        .then(({ data }) => {
          this.isLoading = false;
          this.$router.push(`/payment?booking=${data.booking_id}`);
        }).catch((errors) => {
          this.isLoading = false;
          console.log(errors);
        });
    },

    redirectToPaypal(path) {
      window.location.href = path;
      // this.$router.push('/student-dashboard/calendar');
    },

    cancel(id) {
      this.$emit('cancel', id);
      this.$modal.hide('open-slot');
      return axios.put(`/api/slots/${id}/free`);
    },
  },
};
</script>
